import * as React from 'react';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { getChannelsMetrics } from 'utils/metrics';
import { isMetricsStillFetching } from 'utils/helper/profile';
import { getFormattedFollowersValue, getInfluencerFullName, getNumFollowers } from 'utils/influencers';
import { getFormattedPercentageRate } from 'utils/formatters';
import {
  CHANNELS_WITH_METRICS,
  CHANNEL_TYPE_BLOG,
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE,
  EXTRA_CHANNELS,
  getTiktokEmbedUrl,
  getYoutubeEmbedUrl,
  isChannelHasEnoughFollowers
} from 'utils/channels';
import { isDefined } from 'utils/helper/common';

import type {
  Channel,
  ChannelMetric,
  Channels,
  ImageStandard,
  TabsSectionItem,
  TileStandard,
  TileStandardResidual
} from 'types';

export const NUM_POSTS_TO_SHOW = {
  standard: 8,
  tall: 4
};

const getMetricsLabel = (metric: Partial<ChannelMetric>): string | React.ReactNode => {
  if (isMetricsStillFetching(metric)) {
    return (
      <div className="tile-standard__empty-metrics-tab-label">
        <SVGDynamicIcon
          name="two-gears-custom-fg"
          customColor="grey"
          width={14}
          height={14}
        />
      </div>
    );
  }

  const numFollowers = getNumFollowers(metric.followers);
  const formattedFollowers = getFormattedFollowersValue(numFollowers);
  const formattedPercentage = getFormattedPercentageRate(metric.engagement);

  return `${formattedFollowers} | ${formattedPercentage}`;
};

export const getMainTabs = (channels: Channels): Array<TabsSectionItem> => {
  const metrics = getChannelsMetrics(channels) || [];

  return metrics.map(item => ({
    type: 'main',
    label: getMetricsLabel(item),
    identifier: item.type,
    data: {
      type: 'activeTabMain',
      identifier: item.type
    },
    icon: {
      name: `${item.type}-no-customisation`,
      width: 16,
      height: 16,
      url: item.url
    },
    metrics: {
      followers: item.followers ?? undefined,
      engagementRate: item.engagement ?? undefined
    },
    className: 'tile-standard-tabs__tab'
  }));
};

export const getResidualTabData = (channels: Channels): Array<TileStandardResidual> => {
  const keys = (Object.keys(channels) || []);
  const channelsAsArray = keys.map(key => channels[key] || null);
  const tiles = channelsAsArray.map(channel => {
    const isMain = CHANNELS_WITH_METRICS.includes(channel?.type ?? '');
    const isExtra = EXTRA_CHANNELS.includes(channel?.type ?? '');
    const isBigEnough = isChannelHasEnoughFollowers(channel);
    const isStillFetching = isMetricsStillFetching(channel?.metrics);

    if (
      // Filter main channels with enough followers
      (isMain && isBigEnough)
      // Filter main channels which are still fetching
      || (isMain && isStillFetching)
      // Fillter Tumblr and other non-extra, but not main with small amount of followers
      || (!(isMain && !isBigEnough) && !isExtra)
    ) {
      return null;
    }

    return {
      type: channel?.type,
      url: channel?.url,
      username: channel?.username
    };
  });

  const result: Array<TileStandardResidual> = tiles.filter(isDefined).filter(item => {
    const isValid = item && (isString(item?.username) || !isEmpty(item?.url));
    const isBlog = item && item?.type === CHANNEL_TYPE_BLOG;

    return isValid || isBlog;
  });

  return result;
};

export const getResidualTab = (): TabsSectionItem => ({
  type: 'residual',
  label: '...',
  identifier: 'residual',

  data: {
    type: 'activeTabMain',
    identifier: 'residual'
  },

  className: 'tile-standard-tabs__tab tile-standard-tabs__tab--is-residual'
});

export const getTabs = (channels: Channels): Array<TabsSectionItem> => {
  const mainTabs = getMainTabs(channels);
  const residualTab = getResidualTab();
  const residualTabData = getResidualTabData(channels);

  return !isEmpty(residualTabData)
    ? mainTabs.concat([residualTab])
    : mainTabs;
};

export const getActiveChannel = (channels: Channels, activeTab: string): Channel => channels[activeTab] || { type: 'residual' };

export const getEmbedUrl = (url: string, channel: string): string | undefined | null => {
  if (channel === 'youtube') {
    return getYoutubeEmbedUrl(url);
  }

  if (channel === 'tiktok') {
    return getTiktokEmbedUrl(url);
  }

  return null;
};

export const getIndexStart = (numPostsToShow: number, carouselIndex: number): number => numPostsToShow * carouselIndex;

export const getIndexEnd = (startIndex: number, numPostsToShow: number): number => startIndex + numPostsToShow;

export function getNumPostsToShow(channel: string): number {
  return channel === 'tiktok'
    ? NUM_POSTS_TO_SHOW.tall
    : NUM_POSTS_TO_SHOW.standard;
}

export const getImagesFromStandardImages = (
  standardImages: Array<ImageStandard>,
  numPostsToShow: number,
  carouselIndex: number
): Array<ImageStandard> => {
  const sliceStart = getIndexStart(numPostsToShow, carouselIndex);
  const sliceEnd = getIndexEnd(sliceStart, numPostsToShow);
  return standardImages.slice(sliceStart, sliceEnd);
};

export const getInfluencerName = (creator: TileStandard): string => {
  const name = getInfluencerFullName(creator);

  return (name.length > 0 && name !== creator.name)
    ? name
    : '';
};

export const getLink = (creator: TileStandard, channel?: string): string => {
  const supportedChannels = [CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK, CHANNEL_TYPE_YOUTUBE];
  const channelAnchor = (channel && supportedChannels.includes(channel)) ? `#${channel}` : '';

  return `/content-creator/profiles/${String(creator.id)}${channelAnchor}`;
};

export const getLoadingImages = (numItemsToShow: number): Array<ImageStandard> => new Array(numItemsToShow).fill({
  type: 'loading',
  assetUrl: '',
  code: '',
  url: ''
});

// when freemium, first result is static (COLLAB-12525)
export const getIsBlockedStatus = (
  isFreemium?: boolean,
  tileIndex?: number
): boolean => (
  isFreemium === true && isNumber(tileIndex) && tileIndex > 0
);
